import { useEffect, useCallback } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import {
  mapApiProductToProdutoSelecionado,
  mapProductListItemToProdutoSelecionado,
  reverseCertificationMap
} from './utils';

type Setters = {
  setProdutos: (v: any) => void;
  setPraticaAgroecologica: (v: any) => void;
  setCertificacoes: (v: any) => void;
  setOtherCertifications: (v: any) => void;
  setInitialLoading: (v: any) => void;
  setSnackbar: (v: any) => void;
  setLoadingProducts: (v: any) => void;
  setPracticesConventional: (v: any) => void;
};

export function useProducaoAgroecologicaData(uuid: string | undefined, dadosParciais: any, setters: Setters) {
  const {
    setProdutos,
    setPraticaAgroecologica,
    setCertificacoes,
    setOtherCertifications,
    setInitialLoading,
    setSnackbar,
    setLoadingProducts,
    setPracticesConventional
  } = setters;

  // exported implementation used by the hook but also testable in isolation
  const carregarDadosDaAPI = useCallback(async () => carregarDadosDaAPI_internal(uuid, {
    setProdutos,
    setPraticaAgroecologica,
    setCertificacoes,
    setOtherCertifications,
    setInitialLoading,
    setSnackbar,
    setLoadingProducts: setters.setLoadingProducts,
    setPracticesConventional
  }), [uuid, setProdutos, setPraticaAgroecologica, setCertificacoes, setOtherCertifications, setInitialLoading, setSnackbar, setPracticesConventional, setters.setLoadingProducts]);

  useEffect(() => {
    // delegate to exported implementation for easier testing
    fetchProductsInternal(dadosParciais, {
      setProdutos,
      setPraticaAgroecologica,
      setCertificacoes,
      setOtherCertifications,
      setInitialLoading,
      setSnackbar,
      setLoadingProducts,
      setPracticesConventional
    }, carregarDadosDaAPI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosParciais, carregarDadosDaAPI, setProdutos, setPracticesConventional, setLoadingProducts, setSnackbar]);
}

// exported helpers for unit testing
export async function carregarDadosDaAPI_internal(uuid: string | undefined, setters: Setters) {
  const {
    setProdutos,
    setPraticaAgroecologica,
    setCertificacoes,
    setOtherCertifications,
    setInitialLoading,
    setSnackbar,
    setPracticesConventional
  } = setters;

  if (!uuid) return;
  try {
    const response = await CuffaAPI.getDataByID(uuid);

    if (response && response.dados) {
      const apiData = response.dados;

      setPracticesConventional(apiData.family_practices_conventional_production ?? false);

      if (apiData.products) {
        const produtosDaAPI = apiData.products
          .filter((p: any) => p.management_type === 'CONVENCIONAL')
          .map((p: any) => mapApiProductToProdutoSelecionado(p));

        setProdutos((prev: any[]) => {
          const produtosBase = prev.filter((p: any) => !p.selecionado);
          return [...produtosBase, ...produtosDaAPI];
        });
      }

      const praticaValue = apiData.practices_agroecological_organic ?? apiData.family_practices_agroecological_organic ?? null;
      setPraticaAgroecologica(praticaValue);

      if (praticaValue) {
        let tiposCertificacao: string[] = [];
        let outrasCertificacoes: any[] = [];

        if (apiData.certification_types) {
          tiposCertificacao = apiData.certification_types;
        } else if (apiData.family_certification_types?.types) {
          tiposCertificacao = apiData.family_certification_types.types;
        }

        if (apiData.other_certifications) {
          outrasCertificacoes = apiData.other_certifications;
        } else if (apiData.family_certification_types?.other_certifications) {
          outrasCertificacoes = apiData.family_certification_types.other_certifications;
        }

        const mappedCerts = tiposCertificacao
          .map((cert: string) => {
            const mappedKey = reverseCertificationMap[cert];
            if (mappedKey) return mappedKey;
            if (cert === 'OUTRO') return 'OUTRO';
            if (cert.includes('AUDITORIA')) return 'AUDITORIA';
            if (cert.includes('PARTICIPATIVO') || cert.includes('SPG')) return 'SISTEMA_PARTICIPATIVO';
            if (cert.includes('CONTROLE SOCIAL') || cert.includes('OCS')) return 'CONTROLE_SOCIAL';
            return null;
          })
          .filter((c: string | null): c is string => c !== null);

        setCertificacoes(mappedCerts);
        setOtherCertifications(outrasCertificacoes);
      }
    }
  } catch (error) {
    console.error('Erro ao carregar dados da API:', error);
    setSnackbar({ open: true, message: 'Erro ao carregar dados salvos.', severity: 'error' });
  } finally {
    setInitialLoading(false);
  }
}

export async function fetchProductsInternal(dadosParciais: any, setters: Setters, carregarDadosDaAPI?: () => Promise<void>) {
  const { setProdutos, setPracticesConventional, setLoadingProducts, setSnackbar } = setters;
  try {
    setLoadingProducts(true);
    const response = await CuffaAPI.getProducts(['VEGETAL', 'ANIMAL']);
    const produtosFormatados = response.dados.map((produto: any) => mapProductListItemToProdutoSelecionado(produto));

    setProdutos(produtosFormatados);

    if (dadosParciais?.producaoAgroecologica && produtosFormatados.length > 0) {
      setPracticesConventional(dadosParciais.producaoAgroecologica.practices_conventional_production);

      setProdutos((prev: any[]) => prev.map((produto: any) => {
        const savedProduct = dadosParciais.producaoAgroecologica?.conventional_products?.find(
          (sp: any) => sp.product_id === produto.uuid
        );
        if (!savedProduct) return produto;
        return {
          ...produto,
          name: savedProduct.name || produto.name,
          selecionado: true,
          expandido: false,
          production_area_hectares: savedProduct?.production_area_hectares || null,
          volume_produced: savedProduct?.volume_produced || null,
          volume_produced_unit: savedProduct?.volume_produced_unit,
          volume_commercialized: savedProduct?.volume_commercialized || null,
          volume_commercialized_unit: savedProduct?.volume_commercialized_unit,
          average_commercialized_value: savedProduct?.average_commercialized_value || null,
          volume_self_consumption: savedProduct?.volume_self_consumption || null,
          volume_self_consumption_unit: savedProduct?.volume_self_consumption_unit,
          volume_lost: savedProduct?.volume_lost || null,
          volume_lost_unit: savedProduct?.volume_lost_unit,
          is_processed: savedProduct.is_processed,
          production_type: savedProduct.production_type,
          product_type: savedProduct.product_type,
          availability_months: savedProduct?.availability_months || undefined
        };
      }));
    } else {
      if (carregarDadosDaAPI) await carregarDadosDaAPI();
    }
  } catch (error) {
    console.error('Erro ao buscar produtos:', error);
    setSnackbar({ open: true, message: 'Erro ao carregar produtos disponíveis.', severity: 'error' });
  } finally {
    setLoadingProducts(false);
  }
}
